import { type FC } from 'react';
import { createFragmentContainer, graphql } from 'react-relay/legacy';

import HpLazyImageWrapper from '../HpLazyImageWrapper/HpLazyImageWrapper';
import ResponsiveImage from '../ResponsiveImage';
import HpSharedHeroBannerText from '../HpSharedHeroBannerText/HpSharedHeroBannerText';
import classnames from 'classnames';
import { Link } from 'dibs-elements/exports/Link';

import { type HpSharedSharedHeroBanner_promoItem$data as PromoItem } from './__generated__/HpSharedSharedHeroBanner_promoItem.graphql';

import styles from './HpSharedSharedHeroBanner.scss';

type Props = {
    isVisible: boolean;
    onClick: () => void;
    onVideoStateChange?: () => void;
    useLazyLoadImages?: boolean;
    videoPlaying?: boolean;
    promoItem: PromoItem;
};

const HpSharedSharedHeroBanner: FC<Props> = ({
    isVisible,
    onClick,
    useLazyLoadImages,
    promoItem,
}) => {
    const {
        cmsId,
        imageUrl,
        mobileImageUrl,
        imageHeight = 0,
        imageWidth = 0,
        mobileImageHeight = 0,
        mobileImageWidth = 0,
        linkData = null,
        cta = null,
        heroBannerSettings = null,
    } = promoItem || {};

    const { backgroundColor, bannerImageWidth, contentPosition } = heroBannerSettings || {};
    const isFullWidth = bannerImageWidth === 'full';
    const isReverse = contentPosition === 'right';

    return (
        <div
            className={classnames(styles.contentContainer, {
                [styles.fullWidth]: isFullWidth,
                [styles.reverse]: isReverse,
                [styles.visible]: isVisible,
                [styles.border]: !backgroundColor && !isFullWidth,
            })}
            style={{
                backgroundColor: backgroundColor || undefined,
            }}
        >
            <HpSharedHeroBannerText
                onClick={onClick}
                className={styles.textContainer}
                heroBannerSettings={heroBannerSettings}
                linkData={linkData}
                cta={cta}
            />

            <HpLazyImageWrapper
                className={classnames(styles.imageContainer, {
                    [styles.fullWidth]: isFullWidth,
                })}
                useLazyLoad={useLazyLoadImages}
                imageHeight={mobileImageHeight || imageHeight}
                imageWidth={mobileImageWidth || imageWidth}
            >
                <ResponsiveImage
                    ariaHidden
                    className={classnames(styles.image, {
                        [styles.fullWidth]: isFullWidth,
                    })}
                    src={imageUrl || mobileImageUrl || ''}
                />
            </HpLazyImageWrapper>

            {/* eslint-disable-next-line react/forbid-elements, jsx-a11y/anchor-has-content */}
            <Link
                // this link is redundant with the cta in HpSharedHeroBannerText. do not add
                // label/children, hide from a11y-tree, and prevent keyboard focus.
                ariaHidden
                key={cmsId}
                tabIndex={-1}
                href={linkData?.path || undefined}
                className={classnames(styles.overlayLink)}
                onClick={onClick}
            />
        </div>
    );
};

export const HpSharedPromoHeroItem = createFragmentContainer(HpSharedSharedHeroBanner, {
    promoItem: graphql`
        fragment HpSharedSharedHeroBanner_promoItem on PromoItem {
            cmsId
            ... on PromoItem @skip(if: $isMobile) {
                imageUrl
                imageHeight
                imageWidth
            }
            ... on PromoItem @include(if: $isMobile) {
                mobileImageHeight
                mobileImageWidth
                mobileImageUrl
            }
            linkData {
                path
                ...HpSharedHeroBannerText_linkData
            }
            heroBannerSettings {
                ...HpSharedHeroBannerText_heroBannerSettings
                contentPosition
                backgroundColor
                bannerImageWidth
            }
            cta
        }
    `,
});
